import { FC } from 'react';

import { NormalizedUnitOfMeasure } from '@ravago/shared/page-data/models/contentful-mapped';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { IconButton } from '@ravago/shared/radiance/components/button/components/IconButton/IconButton';
import { Download, DownloadOption } from '@ravago/shared/radiance/components/input-field/components/Download/Download';
import { Dropdown } from '@ravago/shared/radiance/components/input-field/components/Dropdown/Dropdown';
import { useTranslations } from '@ravago/shared/translations/client';

import { RelatedDocumentUlProspectorLink } from './related-document-ul-prospector-link';

interface Props {
   onDownload?: () => void;
   onViewDocument?: () => void;
   onLanguageChange?: (language: DownloadOption) => void;
   onUOMChange?: (uom: DownloadOption) => void;
   showTdsUom?: boolean;
   languages: DownloadOption[];
   selectedLanguage: DownloadOption;
   selectedUOM?: DownloadOption;
   documentId: string;
   language: string;
   ulProspectorId: string | undefined;
   isInternal: boolean;
   superCondensedView?: boolean;
   channelUOMs: Array<NormalizedUnitOfMeasure>;
}

export const RelatedDocumentTdsDownload: FC<Props> = ({
   onUOMChange,
   onLanguageChange,
   language,
   documentId,
   selectedLanguage,
   selectedUOM,
   showTdsUom = false,
   onDownload,
   onViewDocument,
   isInternal,
   channelUOMs,
   ulProspectorId,
   languages,
   superCondensedView = false
}) => {
   const translate = useTranslations();
   const handleLanguageChange = async (lan: DownloadOption | undefined) => {
      if (lan) onLanguageChange?.(lan);
   };

   const handleUOMChange = async (uom: DownloadOption | undefined) => {
      if (uom) onUOMChange?.(uom);
   };

   const handleDownload = () => {
      onDownload?.();
   };

   const handleView = () => {
      onViewDocument?.();
   };

   const download = () => (
      <Box direction="row" gap="sm" items="center">
         <Download
            onChange={(value) => handleLanguageChange(value)}
            onDownload={() => handleDownload()}
            value={selectedLanguage}
            id={`${documentId}-download`}
            options={languages}
         />
         <IconButton icon="eye" variant="on-light" backdrop={false} onClick={() => handleView()} />
      </Box>
   );

   const selectorsAndDownload = () => (
      <>
         <Dropdown
            options={languages}
            value={selectedLanguage}
            emptyOption={false}
            label={translate('products-technical-data-sheet')}
            onChange={(lan) => handleLanguageChange(lan)}
            id={`${documentId}-language`}
         />
         <Dropdown
            options={channelUOMs}
            value={selectedUOM}
            emptyOption={false}
            label={translate('product.documents.uom')}
            onChange={(uom) => handleUOMChange(uom)}
            id={`${documentId}-uom`}
         />
         <IconButton icon="download" variant="on-primary" backdrop onClick={() => handleDownload()} />
         <IconButton icon="eye" variant="on-light" backdrop={false} onClick={() => handleView()} />
      </>
   );

   return (
      <Box direction="column" gap="xs" items="start">
         <Box
            direction={superCondensedView ? 'column' : 'row'}
            gap={superCondensedView ? 'md' : 'xs'}
            width={superCondensedView ? '100%' : undefined}
            items={superCondensedView ? 'end' : 'start'}
         >
            {showTdsUom ? selectorsAndDownload() : download()}
            {superCondensedView && ulProspectorId && isInternal && (
               <RelatedDocumentUlProspectorLink language={language} ulProspectorId={ulProspectorId} centered />
            )}
         </Box>
      </Box>
   );
};
